import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CAlert, CBadge, CButton, CSpinner } from '@coreui/react';
import { getApi } from '../../apis/backendApis';
import {
    InfraHistorico,
    InfraRun,
    InfraSubpasso,
    RUN_ERRO,
    RUN_EXECUTANDO,
    RUN_PENDENTE,
    RUN_PULADO,
    classeSituacao,
    duracao,
    marcadorSituacao
} from '../../models/InfraRun';
import { pintarAnsi, semAnsi } from './ansi';

interface Props {
    pipelineId: string;
    chave: string;
    /** Muda quando o passo avança, para recarregar o histórico. */
    versao: number;
    /** Sobe a cada Executar: a atenção volta para a tentativa que está nascendo. */
    execucaoIniciada: number;
    /**
     * Dispara outra tentativa. Fica junto das tentativas, não num rodapé.
     * Com `retomar`, pula o que a tentativa anterior já fechou.
     */
    onExecutar?: (retomar?: boolean) => void;
    /** Este passo, e não outro, está executando: muda o texto do botão. */
    executando?: boolean;
    /** Algum passo do pipeline está executando: só desabilita o botão. */
    ocupado?: boolean;
    /** O passo terminou com erro: retomar de onde parou passa a fazer sentido. */
    comErro?: boolean;
}

/**
 * Histórico de execuções de um passo, no modelo de um job de CI: as tentativas
 * na direita, e a tentativa selecionada aberta em subpassos com o comando que
 * rodou, onde rodou e quanto levou.
 */
/**
 * Formata o JSON que aparece dentro do comando ou do retorno.
 *
 * O corpo de uma chamada de API cabe numa linha só, mas ninguém lê assim. Aqui
 * o trecho entre a primeira chave e a última vira JSON indentado, e o que vem
 * antes (o verbo e a rota, por exemplo) continua onde estava.
 */
const comJsonLegivel = (texto: string | null): string => {
    if (!texto) {
        return '';
    }

    const inicio = texto.search(/[{[]/);
    const fim = Math.max(texto.lastIndexOf('}'), texto.lastIndexOf(']'));
    if (inicio === -1 || fim <= inicio) {
        return texto;
    }

    try {
        const bonito = JSON.stringify(JSON.parse(texto.slice(inicio, fim + 1)), null, 2);
        return texto.slice(0, inicio) + bonito + texto.slice(fim + 1);
    } catch (erro) {
        // Não era JSON: o texto segue como veio.
        return texto;
    }
};

/**
 * Limpa a saída para ela ficar igual à do terminal.
 *
 * O governor e os passos prefixam linha de resultado com "-> ", que ajudava
 * quando cada subpasso era um bloco rotulado. Num terminal isso é ruído: o que
 * se quer ler é o que o comando imprimiu, sem enfeite.
 */
const saidaPura = (texto: string | null): string => {
    if (!texto) {
        return '';
    }
    return texto
        .split('\n')
        // O "-> " pode vir depois de uma sequencia de cor, entao o corte
        // olha tambem essa forma, e nao so o comeco cru da linha.
        // eslint-disable-next-line no-control-regex
        .map(linha => linha.replace(/^((?:\x1b\[[0-9;]*m)*)\s*->\s?/, '$1'))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

/**
 * Linha final no formato do shell.
 *
 * O código vem da saída quando o comando informou ("exit status 255"); sem
 * isso, sai do estado do subpasso, que é o que o pipeline sabe.
 */
const linhaDeSaida = (sub: InfraSubpasso): string | null => {
    if (sub.situacao === RUN_EXECUTANDO || sub.situacao === RUN_PENDENTE) {
        return null;
    }
    if (sub.situacao === RUN_PULADO) {
        return 'pulado - nada a fazer';
    }

    const achado = /exit (?:status|code)[ :]+(\d+)/i.exec(semAnsi(sub.saida));
    if (achado) {
        const codigo = Number(achado[1]);
        return 'exit(' + codigo + ')' + (codigo === 0 ? ' - sucesso ✅' : ' - erro ❌');
    }

    return sub.situacao === RUN_ERRO ? 'exit(1) - erro ❌' : 'exit(0) - sucesso ✅';
};

/** Só a hora, para a linha do subpasso: a data completa fica no detalhe. */
const horaDe = (inicio: string | null): string => {
    if (!inicio) {
        return '';
    }
    const partes = inicio.split(' ');
    return partes.length > 1 ? partes[1] : inicio;
};

const StepRuns = (props: Props) => {
    const { pipelineId, chave, versao, execucaoIniciada, onExecutar, executando, ocupado, comErro } = props;

    // Faz o cronômetro do subpasso em curso andar entre uma busca e outra.
    const [, setTique] = useState<number>(0);

    /** Id da tentativa do topo na carga anterior, para detectar uma nova. */
    const topoRef = useRef<number | null>(null);

    /** Tentativa que se quer detalhada; null deixa o servidor escolher. */
    const pedidoRef = useRef<number | null>(null);

    /** Valor do contador de execuções na volta anterior, para ver a mudança. */
    const execucaoRef = useRef<number | null>(null);

    /** Entre o clique em Executar e a tentativa aparecer no banco. */
    const [iniciando, setIniciando] = useState<boolean>(false);

    const [dados, setDados] = useState<InfraHistorico | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);
    const [runSelecionado, setRunSelecionado] = useState<number | null>(null);
    const [abertos, setAbertos] = useState<{ [indice: number]: boolean }>({});

    const carregar = useCallback((idDesejado?: number) => {
        setCarregando(true);
        const alvo = idDesejado !== undefined ? (idDesejado || null) : pedidoRef.current;

        getApi().get('/infra/pipeline/' + pipelineId + '/step/' + chave + '/runs',
            alvo ? { params: { run: alvo } } : undefined)
            .then(resposta => {
                setDados(resposta.data);
                setErro(null);
                const runs: Array<InfraRun> = resposta.data.runs || [];
                const emCurso = runs.filter(r => r.situacao === RUN_EXECUTANDO)[0];
                const topo = runs.length > 0 ? runs[0] : null;
                const novaTentativa = topo && topoRef.current !== null && topo.id !== topoRef.current;

                // Fora do updater de propósito: aqui a decisão vale já para a
                // próxima busca. Dentro dele, o pedido velho ainda iria junto.
                if (emCurso || novaTentativa) {
                    const alvo = emCurso || topo;
                    const precisaDetalhe = alvo && !alvo.carregado;

                    pedidoRef.current = null;
                    setRunSelecionado(alvo ? alvo.id : null);
                    setIniciando(false);

                    // A resposta detalhou outra tentativa: busca de novo, senão
                    // a nova aparece vazia até a próxima volta.
                    if (precisaDetalhe) {
                        topoRef.current = topo ? topo.id : null;
                        carregar(0);
                        return;
                    }
                } else {
                    setRunSelecionado(atual => runs.some(r => r.id === atual) ? atual : (topo ? topo.id : null));
                }

                topoRef.current = topo ? topo.id : null;
            })
            .catch(motivo => {
                const daApi = String(motivo?.response?.data?.error || '');
                // Sessão vencida já redireciona para o login no interceptor; a
                // mensagem aqui só serviria para confundir.
                if (daApi.indexOf('precisa de um token válido') !== -1) {
                    setErro('Sessão expirada. Entrando de novo...');
                    return;
                }
                setErro(daApi || 'Não consegui carregar o histórico.');
            })
            .finally(() => setCarregando(false));
    }, [pipelineId, chave]);

    useEffect(() => { carregar(); }, [carregar, versao]);

    // Clicou em Executar: a tentativa antiga sai da frente na hora e o roteiro
    // do passo aparece como quem vai começar.
    useEffect(() => {
        // O contador é do pipeline inteiro, não deste passo: ao abrir um passo
        // qualquer ele já vem com o valor de execuções anteriores. Só uma
        // mudança de verdade, com o passo aberto, significa "começou agora".
        const anterior = execucaoRef.current;
        execucaoRef.current = execucaoIniciada;

        if (execucaoIniciada === 0 || anterior === null || anterior === execucaoIniciada) {
            return;
        }
        pedidoRef.current = null;
        setIniciando(true);

        // Teto de espera. O "iniciando" some quando a tentativa nova aparece,
        // mas se o backend recusou a execucao ela nunca aparece, e sem este
        // limite o spinner girava para sempre dizendo que algo estava por
        // comecar.
        const desistir = window.setTimeout(() => setIniciando(false), 15000);
        return () => window.clearTimeout(desistir);
    }, [execucaoIniciada]);

    const emCurso = !!dados && dados.runs.some(r => r.situacao === RUN_EXECUTANDO);
    useEffect(() => {
        if (!emCurso) {
            return;
        }
        const relogio = window.setInterval(() => setTique(t => t + 1), 250);
        return () => window.clearInterval(relogio);
    }, [emCurso]);

    // Ao trocar de passo, nada deve continuar expandido do passo anterior.
    // O "iniciando" também é do passo que ficou para trás: sem limpar, o passo
    // novo abria dizendo que uma execução estava começando, sem nunca ter sido
    // executado.
    useEffect(() => {
        setAbertos({});
        setIniciando(false);
        pedidoRef.current = null;
        topoRef.current = null;
        execucaoRef.current = null;
    }, [chave]);

    const renderSubpasso = (sub: InfraSubpasso, indice: number) => {
        const rodando = sub.situacao === RUN_EXECUTANDO;
        const temDetalhe = !!(sub.comando || sub.saida || sub.local || sub.inicio);

        // O que está rodando abre sozinho: é onde o olho tem que estar.
        const aberto = rodando ? abertos[indice] !== false : !!abertos[indice];

        // Enquanto não fecha não há duração gravada, então o tempo corre daqui.
        const decorrido = rodando && sub.inicio
            ? Date.now() - Date.parse(sub.inicio.replace(
                /^(\d{2})\/(\d{2})\/(\d{4}) /, '$3-$2-$1T'))
            : null;

        return (
            <div key={indice} className={'infra-sub infra-sub-' + classeSituacao(sub.situacao)}>
                <button
                    type="button"
                    className="infra-sub-cabeca"
                    onClick={() => setAbertos(atual => ({ ...atual, [indice]: !aberto }))}
                    disabled={!temDetalhe}
                >
                    <span className="infra-sub-hora">{horaDe(sub.inicio)}</span>
                    <span className="infra-sub-marcador">
                        {rodando ? <CSpinner size="sm" /> : marcadorSituacao(sub.situacao)}
                    </span>
                    <span className="infra-sub-seta">{temDetalhe ? (aberto ? '▾' : '▸') : ' '}</span>
                    <span className="infra-sub-nome">{sub.nome}</span>
                    <span className="infra-sub-ms">
                        {rodando
                            ? (decorrido !== null && decorrido > 0 ? duracao(decorrido) : '…')
                            : duracao(sub.ms)}
                    </span>
                </button>

                {aberto && (
                    <div className="infra-terminal">
                        {/* Formato de terminal: [onde] # comando, a saída pura
                            embaixo e o código de saída na última linha. Rótulo
                            para cada pedaço só afastava o olho do que importa. */}
                        <pre className="infra-terminal-texto">
                            {sub.local && <span className="infra-terminal-onde">[{sub.local}]</span>}
                            {sub.local && ' '}
                            {sub.comando && <span className="infra-terminal-prompt"># </span>}
                            {sub.comando && <span className="infra-terminal-comando">{semAnsi(comJsonLegivel(sub.comando))}</span>}
                            {(sub.local || sub.comando) && '\n'}
                            {saidaPura(comJsonLegivel(sub.saida)) && '\n'}
                            {pintarAnsi(saidaPura(comJsonLegivel(sub.saida)), 'sub' + indice)}
                            {saidaPura(comJsonLegivel(sub.saida)) && '\n'}
                            {linhaDeSaida(sub) && (
                                <span className={sub.situacao === RUN_ERRO ? 'infra-terminal-exit-erro' : 'infra-terminal-exit'}>
                                    {linhaDeSaida(sub)}
                                </span>
                            )}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    // O mesmo botao serve para a primeira execucao e para as seguintes: o que
    // muda e so o texto. Fica aqui em cima porque a tela sem nenhuma tentativa
    // tambem precisa dele, e foi justamente o que faltou quando as acoes sairam
    // do rodape do modal.
    const ocupadoAgora = !!ocupado || iniciando;
    const rotuloOcupado = executando || iniciando ? 'executando…' : null;

    const botaoExecutar = (primeira: boolean) => onExecutar && (
        <button
            type="button"
            className="infra-runs-acao infra-runs-acao-principal"
            onClick={() => onExecutar(false)}
            disabled={ocupadoAgora}
            title={ocupado && !executando ? 'Outro passo está executando' : undefined}
        >
            {rotuloOcupado || (primeira ? 'executar' : (comErro ? 'executar do zero' : 'executar novamente'))}
        </button>
    );

    // Passo com erro ganha o botão que continua de onde parou. É o que se quer
    // na maioria das falhas: o subpasso que quebrou é o último de vários, e
    // refazer os anteriores custa tempo e repete trabalho que já deu certo.
    const botaoRetomar = () => onExecutar && comErro && (
        <button
            type="button"
            className="infra-runs-acao infra-runs-acao-principal"
            onClick={() => onExecutar(true)}
            disabled={ocupadoAgora}
            title="Pula os subpassos que já concluíram e continua do que falhou"
        >
            {rotuloOcupado || 'tentar novamente'}
        </button>
    );

    if (carregando && !dados) {
        return <div className="infra-runs-carregando"><CSpinner size="sm" /> carregando execuções…</div>;
    }
    // Erro sem dados nenhum: só o aviso. Havendo dados, o aviso entra acima
    // deles, porque apagar o que já estava na tela esconde justamente o que se
    // estava olhando.
    if (erro && !dados) {
        return <CAlert color="danger" className="py-2 mb-0">{erro}</CAlert>;
    }
    if (!dados || dados.runs.length === 0) {
        const roteiro = dados ? (dados.roteiro || []) : [];

        if (roteiro.length === 0) {
            return (
                <div className="infra-runs">
                    <div className="infra-runs-barra">
                        <div className="infra-runs-vazio">Este passo ainda não foi executado.</div>
                        <div className="infra-runs-meta">{botaoExecutar(true)}</div>
                    </div>
                </div>
            );
        }

        return (
            <div className="infra-runs">
                <div className="infra-runs-barra">
                    <div className="infra-runs-vazio">
                        Ainda não executado. Os {roteiro.length} subpassos previstos:
                    </div>
                    <div className="infra-runs-meta">{botaoExecutar(true)}</div>
                </div>
                <div className="infra-subs">
                    {roteiro.map((nome, indice) => renderSubpasso({
                        nome, situacao: 1, inicio: null, local: null,
                        comando: null, saida: null, ms: null
                    }, indice))}
                </div>
            </div>
        );
    }

    const run = dados.runs.filter(r => r.id === runSelecionado)[0] || dados.runs[0];

    // O passo mostra o roteiro inteiro, sempre: rodando, concluído ou com erro.
    // Quem falhou no terceiro de oito precisa continuar vendo os cinco que
    // ficaram para trás. Quem manda é o que foi executado; subpasso fora do
    // roteiro entra do mesmo jeito, e o que o roteiro previa e não veio aparece
    // apagado no fim.
    // Tentativa antiga ainda sem os subpassos carregados nao vira "tudo
    // pendente": os dados dela estao a caminho.
    // Iniciando: a tentativa nova ainda nao existe no banco, entao a lista e o
    // roteiro inteiro, para a tela nao ficar vazia entre o clique e o primeiro
    // subpasso.
    const base = iniciando ? [] : run.subpassos;
    const pendentes: Array<InfraSubpasso> = ((iniciando || run.carregado) ? (dados.roteiro || []) : [])
        .slice(base.length)
        .map(nome => ({
            nome, situacao: 1, inicio: null, local: null, comando: null, saida: null, ms: null
        }));

    return (
        <div className="infra-runs">
            {erro && <CAlert color="danger" className="py-1 px-2 mb-2">{erro}</CAlert>}
            <div className="infra-runs-barra">
                <div className="infra-runs-tentativas">
                    {dados.runs.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            className={'infra-run-chip infra-run-' + classeSituacao(item.situacao)
                                + (item.id === run.id ? ' infra-run-ativo' : '')}
                            onClick={() => {
                                setRunSelecionado(item.id);
                                if (!item.carregado) {
                                    pedidoRef.current = item.id;
                                    carregar(item.id);
                                }
                            }}
                            title={(item.inicio || '') + (item.usuario ? ' · ' + item.usuario : '')}
                        >
                            {marcadorSituacao(item.situacao)} #{item.numero}
                            {item.inicio && (
                                <span className="infra-run-chip-quando">{item.inicio.slice(0, 16)}</span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="infra-runs-meta">
                    {iniciando && <CBadge color="info"><CSpinner size="sm" className="mr-1" />iniciando</CBadge>}
                    {run.situacao === RUN_EXECUTANDO && (
                        <CBadge color="info"><CSpinner size="sm" className="mr-1" />em curso</CBadge>
                    )}
                    <span className="infra-runs-meta-quem">
                        {run.usuario && <span>{run.usuario}</span>}
                        <strong>{duracao(run.ms)}</strong>
                    </span>
                    {/* Sem passar o evento: onClick={carregar} entregava o
                        SyntheticEvent como id da tentativa, o axios tentava
                        serializar um objeto circular e a busca morria antes de
                        sair, virando "não consegui carregar o histórico". */}
                    <button type="button" className="infra-runs-acao" onClick={() => carregar()}>
                        recarregar
                    </button>
                    {botaoRetomar()}
                    {botaoExecutar(false)}
                </div>
            </div>

            {/* O motivo da falha, quando a tentativa terminou em erro.
                O backend sempre mandou e a tela nunca mostrava: quando o passo
                quebrava fora de um subpasso — um fatal do PHP, por exemplo —
                a lista aparecia com os primeiros concluidos e nenhuma
                explicacao, e so o banco dizia o que aconteceu. */}
            {run.erro && (
                <div className="infra-runs-erro">
                    <span className="infra-runs-erro-marca">✕</span>
                    <span>{semAnsi(run.erro)}</span>
                </div>
            )}

            {base.length === 0 && pendentes.length === 0
                ? <div className="infra-runs-vazio">Sem subpassos registrados nesta tentativa.</div>
                : (
                    <div className="infra-subs">
                        {base.concat(pendentes).map(renderSubpasso)}
                    </div>
                )}

            {Object.keys(run.parametros || {}).length > 0 && (
                <details className="infra-runs-params">
                    <summary>Parâmetros desta tentativa</summary>
                    <pre>{JSON.stringify(run.parametros, null, 2)}</pre>
                </details>
            )}
        </div>
    );
};

export default StepRuns;
